import type { FixedSection } from './SectionedChecklist.js';

/**
 * The venue checklist's configuration (§27).
 *
 * Roy's eighteen items, arranged the way a client works through them, each
 * with one line on what BCE actually needs. The keys match the seeded rows;
 * the sections, order and hints are presentation only, so they can change
 * without touching a stored task.
 */

/** §27's six statuses, as Roy listed them, in the client's words. */
export const VENUE_STATUS = {
  notStarted: 'NOT_STARTED',
  inProgress: 'IN_PROGRESS',
  submitted: 'SUBMITTED',
  confirmed: 'CONFIRMED',
  notRequired: 'NOT_REQUIRED',
  overdue: 'OVERDUE',
} as const;

export const VENUE_STATUSES: readonly (readonly [string, string])[] = [
  [VENUE_STATUS.notStarted, 'Not started'],
  [VENUE_STATUS.inProgress, 'In progress'],
  [VENUE_STATUS.submitted, 'Asked the venue'],
  [VENUE_STATUS.confirmed, 'Confirmed'],
  [VENUE_STATUS.notRequired, 'Not needed'],
  [VENUE_STATUS.overdue, 'Overdue'],
];

/** Confirmed and not-needed both count as dealt with. */
export const VENUE_SETTLED: ReadonlySet<string> = new Set([
  VENUE_STATUS.confirmed,
  VENUE_STATUS.notRequired,
]);

export const VENUE_SECTIONS: readonly FixedSection[] = [
  {
    title: 'The room',
    who: 'Usually your venue',
    items: [
      { key: 'tablesChairs', hint: 'Enough seating for everyone in teams, plus a table for materials.' },
      { key: 'stageRisers', hint: 'Only for larger groups, so the facilitator can be seen from the back.' },
      { key: 'electrical', hint: 'Power where the facilitator, the AV and any charging stations will be.' },
    ],
  },
  {
    title: 'Sound, video and internet',
    who: 'Usually your venue or AV provider',
    items: [
      { key: 'microphone', hint: 'A wireless handheld or lapel mic for the facilitator.' },
      { key: 'soundSystem', hint: 'Speakers that reach the whole room, with an input for our audio.' },
      { key: 'projection', hint: 'A screen and projector or display, with an HDMI input.' },
      { key: 'internet', hint: 'Wi-Fi that can handle the group, or a wired line for the facilitator.' },
      { key: 'technicalSupport', hint: 'Someone on site who can fix sound or screens if they fail.' },
    ],
  },
  {
    title: 'Materials in and out',
    who: 'Usually your venue',
    items: [
      { key: 'receivingContact', hint: 'Who signs for our shipment when it arrives, and their number.' },
      { key: 'preEventStorage', hint: 'Somewhere secure to keep the shipment until the day.' },
      { key: 'materialsMovement', hint: 'Getting the boxes from storage into the room before we set up.' },
      { key: 'postEventStorage', hint: 'Somewhere to hold anything going back to BCE.' },
      { key: 'materialsDisposal', hint: 'How packaging and leftover materials leave the building.' },
    ],
  },
  {
    title: 'Access and parking',
    who: 'Usually your venue; the charity items are yours or the beneficiary’s',
    items: [
      { key: 'venueContact', hint: 'The person BCE calls on the day if something needs sorting.' },
      { key: 'loadingDock', hint: 'Where deliveries go, the hours, and anything a driver needs to know.' },
      { key: 'staffParking', hint: 'Parking or vouchers for the BCE team on the day.' },
      { key: 'charityParking', hint: 'A space near the door for the beneficiary’s vehicle.' },
      { key: 'donationTransport', hint: 'How the finished donations get from the room to the beneficiary.' },
    ],
  },
];
